import React, { useState } from 'react';
import { 
  Box, 
  AppBar, 
  Toolbar, 
  Typography, 
  Container, 
  IconButton, 
  Drawer, 
  List, 
  ListItem, 
  ListItemButton,
  ListItemIcon, 
  ListItemText,
  Divider,
  Avatar,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField
} from '@mui/material';
import { 
  Menu as MenuIcon, 
  Home as HomeIcon, 
  Search as SearchIcon, 
  QueueMusic as PlaylistsIcon, 
  Chat as ChatIcon,
  Settings as SettingsIcon,
  SmartToy as SmartToyIcon,
  Add as AddIcon,
  Headphones as HeadphonesIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useSpotify } from '../../context';
import MobileNavigation from './MobileNavigation';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const menuItems = [
    { text: 'Home', icon: <HomeIcon />, path: '/home' },
    { text: 'Search', icon: <SearchIcon />, path: '/search' },
    { text: 'Playlists', icon: <PlaylistsIcon />, path: '/playlists' },
    { text: 'AI Chat', icon: <ChatIcon />, path: '/ai' },
    { text: 'Playlist Agent', icon: <SmartToyIcon />, path: '/playlist-agent' },
    { text: 'DJ Playlist', icon: <HeadphonesIcon />, path: '/dj-playlist' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
  ];

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const drawer = (
    <Box sx={{ width: 250 }}>
      {/* Add toolbar placeholder to push content below AppBar */}
      <Toolbar />
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          py: 2 
        }}
      >
        <Box 
          component="img"
          src="/assets/otto_logo.png"
          alt="Otto Logo"
          sx={{ width: 220, height: 220, mb: 1 }}
        />
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Tunes with Otto
        </Typography>
      </Box>
      
      <Divider />
      
      {user && (
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
          <Avatar 
            src={user.images?.[0]?.url} 
            alt={user.displayName || 'User'}
            sx={{ mr: 2 }}
          />
          <Box>
            <Typography variant="subtitle2" noWrap>
              {user.displayName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {user.email}
            </Typography>
          </Box>
        </Box>
      )}
      
      <Divider />
      
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              onClick={() => handleNavigation(item.path)}
              selected={location.pathname === item.path}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ color: location.pathname === item.path ? 'primary.main' : 'inherit' }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  // State for create playlist dialog
  const [createPlaylistDialogOpen, setCreatePlaylistDialogOpen] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [newPlaylistDescription, setNewPlaylistDescription] = useState('');
  const { createPlaylist } = useSpotify();

  const handleCreatePlaylist = async () => {
    if (newPlaylistName.trim()) {
      try {
        await createPlaylist(newPlaylistName, newPlaylistDescription);
        setCreatePlaylistDialogOpen(false);
        setNewPlaylistName('');
        setNewPlaylistDescription('');
        // Navigate to playlists page to see the new playlist
        navigate('/playlists');
      } catch (error) {
        console.error('Error creating playlist:', error);
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          boxShadow: 1
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {menuItems.find(item => item.path === location.pathname)?.text || 'Tunes with Otto'}
          </Typography>
        </Toolbar>
      </AppBar>
      
      <Box
        component="nav"
        sx={{ width: { md: 250 }, flexShrink: { md: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={drawerOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { width: 250, boxSizing: 'border-box' },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      <Box
        component="main"
        className="main-content"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { md: `calc(100% - 250px)` },
          mt: '64px', // AppBar height
        }}
      >
        <Container maxWidth="lg">
          {children}
        </Container>
      </Box>

      {/* Mobile Bottom Navigation - only shown on mobile */}
      {isMobile && (
        <MobileNavigation 
          onAddPlaylistClick={() => setCreatePlaylistDialogOpen(true)} 
        />
      )}

      {/* Create Playlist Dialog */}
      <Dialog 
        open={createPlaylistDialogOpen} 
        onClose={() => setCreatePlaylistDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create New Playlist</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="Playlist Name"
            type="text"
            fullWidth
            variant="outlined"
            value={newPlaylistName}
            onChange={(e) => setNewPlaylistName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            id="description"
            label="Description (optional)"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={newPlaylistDescription}
            onChange={(e) => setNewPlaylistDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatePlaylistDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleCreatePlaylist} 
            variant="contained" 
            startIcon={<AddIcon />}
            disabled={!newPlaylistName.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Layout;
